import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mic, Square, Volume2, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Live decibel meter + audio recorder.
 *
 * NOTE: Browser microphones are uncalibrated, so the values are a
 * relative approximation of dB SPL, not an absolute measurement.
 * We compute RMS from getFloatTimeDomainData and map to a 30-130 dB
 * display range using: display = 20*log10(rms) + 100 (clamped).
 *
 * Props:
 *  - onComplete({ reading, evidence }): fired after recording stops and
 *    the audio is uploaded. `reading` has peak/avg/min/duration/samples.
 *    `evidence` is the uploaded file record (id/path/kind/filename).
 */
export default function DecibelMeter({ onComplete }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | done | error
  const [currentDb, setCurrentDb] = useState(0);
  const [peakDb, setPeakDb] = useState(0);
  const [avgDb, setAvgDb] = useState(0);
  const [minDb, setMinDb] = useState(130);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null); // { reading, evidence }

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const samplesRef = useRef([]);
  const startedAtRef = useRef(0);
  const sumRef = useRef(0);
  const countRef = useRef(0);
  const peakRef = useRef(0);
  const minRef = useRef(130);
  const lastSampleAtRef = useRef(0);

  const cleanup = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  const tick = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    let sumSq = 0;
    for (let i = 0; i < buf.length; i++) sumSq += buf[i] * buf[i];
    const rms = Math.sqrt(sumSq / buf.length);
    let db = 20 * Math.log10(Math.max(rms, 1e-6)) + 100;
    if (db < 30) db = 30;
    if (db > 130) db = 130;
    db = Math.round(db * 10) / 10;

    setCurrentDb(db);
    sumRef.current += db;
    countRef.current += 1;
    if (db > peakRef.current) { peakRef.current = db; setPeakDb(db); }
    if (db < minRef.current) { minRef.current = db; setMinDb(db); }
    const avg = Math.round((sumRef.current / countRef.current) * 10) / 10;
    setAvgDb(avg);

    const now = performance.now();
    setElapsed(Math.floor((now - startedAtRef.current) / 1000));
    // Sample every 250 ms
    if (now - lastSampleAtRef.current > 250) {
      samplesRef.current.push({ t: Math.round((now - startedAtRef.current) / 100) / 10, db });
      lastSampleAtRef.current = now;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;

      // MediaRecorder for actual audio evidence
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = handleRecordingStop;
      recorderRef.current = rec;
      rec.start(500);

      // reset state
      samplesRef.current = [];
      sumRef.current = 0;
      countRef.current = 0;
      peakRef.current = 0;
      minRef.current = 130;
      setPeakDb(0);
      setAvgDb(0);
      setMinDb(130);
      setElapsed(0);
      startedAtRef.current = performance.now();
      lastSampleAtRef.current = 0;
      setState("recording");
      tick();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Microphone access denied");
      setState("error");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const handleRecordingStop = async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setState("uploading");

    const duration = Math.max(1, Math.floor((performance.now() - startedAtRef.current) / 1000));
    const reading = {
      peak_db: peakRef.current,
      avg_db: Math.round((sumRef.current / Math.max(1, countRef.current)) * 10) / 10,
      min_db: minRef.current === 130 ? 0 : minRef.current,
      duration_sec: duration,
      captured_at: new Date().toISOString(),
      samples: samplesRef.current.slice(0, 400), // cap
    };

    // Upload the audio blob
    let evidence = null;
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      if (blob.size > 0) {
        const file = new File([blob], `noise-recording-${Date.now()}.webm`, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evidence = data;
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not attach recording — reading saved");
    }

    // Stop mic stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
    }

    const finalResult = { reading, evidence };
    setResult(finalResult);
    setState("done");
    onComplete?.(finalResult);
    toast.success(`Reading captured — peak ${reading.peak_db} dB`);
  };

  const clear = () => {
    setResult(null);
    setState("idle");
    setCurrentDb(0);
    setPeakDb(0);
    setAvgDb(0);
    setMinDb(130);
    setElapsed(0);
  };

  const pct = Math.max(0, Math.min(100, ((currentDb - 30) / 100) * 100));
  const tone = currentDb >= 75 ? "bg-red-500" : currentDb >= 55 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="rounded-lg border border-border bg-slate-50 p-5 space-y-4" data-testid="decibel-meter">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline text-slate-500">Live decibel meter</div>
          <div className="text-xs text-muted-foreground mt-1">
            Uncalibrated microphone reading — useful as relative evidence, not a legal instrument.
          </div>
        </div>
        {state === "recording" && (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-100 text-red-800 text-xs font-medium" data-testid="dm-recording-indicator">
            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> REC · {elapsed}s
          </span>
        )}
      </div>

      {state !== "done" && (
        <div className="space-y-3">
          <div className="flex items-end gap-3">
            <div className="font-display font-bold text-5xl tracking-tight text-slate-900 tabular-nums" data-testid="dm-current-db">
              {state === "idle" ? "—" : currentDb.toFixed(1)}
            </div>
            <div className="text-sm text-muted-foreground pb-2">dB</div>
          </div>
          <div className="h-3 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full ${tone} transition-all duration-100`} style={{ width: `${pct}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Peak" value={peakDb || 0} testid="dm-peak" />
            <Stat label="Average" value={avgDb || 0} testid="dm-avg" />
            <Stat label="Min" value={minDb === 130 ? 0 : minDb} testid="dm-min" />
          </div>
        </div>
      )}

      {state === "done" && result && (
        <ReadingSummary reading={result.reading} evidence={result.evidence} />
      )}

      <div className="flex flex-wrap gap-2">
        {state === "idle" && (
          <Button type="button" onClick={start} className="gap-2" data-testid="dm-start-btn">
            <Mic className="w-4 h-4" /> Start measuring
          </Button>
        )}
        {state === "recording" && (
          <Button type="button" onClick={stop} variant="destructive" className="gap-2" data-testid="dm-stop-btn">
            <Square className="w-4 h-4" /> Stop &amp; capture
          </Button>
        )}
        {state === "uploading" && (
          <Button type="button" disabled className="gap-2" data-testid="dm-uploading-btn">
            <Loader2 className="w-4 h-4 animate-spin" /> Saving recording...
          </Button>
        )}
        {state === "done" && (
          <Button type="button" onClick={clear} variant="outline" className="gap-2" data-testid="dm-record-again-btn">
            <Mic className="w-4 h-4" /> Record again
          </Button>
        )}
        {state === "error" && (
          <Button type="button" onClick={start} variant="outline" className="gap-2" data-testid="dm-retry-btn">
            <Mic className="w-4 h-4" /> Retry
          </Button>
        )}
      </div>

      <CPCBLimits currentDb={state === "done" ? result.reading.peak_db : currentDb} />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md bg-white border border-border p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display font-semibold text-xl tabular-nums">{value}<span className="text-xs text-muted-foreground ml-1">dB</span></div>
    </div>
  );
}

function CPCBLimits({ currentDb }) {
  const rows = [
    { zone: "Silence zone", day: 50, night: 40 },
    { zone: "Residential", day: 55, night: 45 },
    { zone: "Commercial", day: 65, night: 55 },
    { zone: "Industrial", day: 75, night: 70 },
  ];
  return (
    <div className="rounded-md bg-white border border-border p-3 text-xs">
      <div className="overline text-slate-500 mb-2">CPCB permissible limits (India) — Noise Rules, 2000</div>
      <table className="w-full text-slate-700">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="py-1 pr-2">Area</th>
            <th className="py-1 pr-2">Day dB(A)</th>
            <th className="py-1 pr-2">Night dB(A)</th>
            <th className="py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const violates = currentDb > r.day;
            return (
              <tr key={r.zone} className="border-t border-border/60">
                <td className="py-1.5 pr-2">{r.zone}</td>
                <td className="py-1.5 pr-2 tabular-nums">{r.day}</td>
                <td className="py-1.5 pr-2 tabular-nums">{r.night}</td>
                <td className="py-1.5">
                  {currentDb > 0 && (
                    <span className={`inline-flex items-center gap-1 ${violates ? "text-red-700" : "text-emerald-700"}`}>
                      {violates ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                      {violates ? "Exceeds" : "Within"}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReadingSummary({ reading, evidence }) {
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 space-y-3" data-testid="dm-result-summary">
      <div className="flex items-center gap-2 text-emerald-900">
        <Volume2 className="w-4 h-4" />
        <div className="font-display font-semibold">Reading captured</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Peak" value={reading.peak_db} testid="dm-summary-peak" />
        <Stat label="Average" value={reading.avg_db} testid="dm-summary-avg" />
        <Stat label="Duration" value={`${reading.duration_sec}s`} testid="dm-summary-duration" />
      </div>
      {evidence && (
        <div className="text-xs text-emerald-900">
          Audio recording attached automatically ({evidence.filename}).
        </div>
      )}
    </div>
  );
}
